import './TeamSettings.css';
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { statusText } from '@machs/shared';
import { api, hasSession, refreshUrlToken, setGuestToken, type Me } from '../lib/api';
import { SyncProvider, useSync } from '../lib/sync';
import { Icon } from '../lib/icons';
import { avatarGradient, initials, listTime } from '../lib/format';
import { imageUrl } from '../lib/images';
import { ChatView } from './ChatView';
import { TaskDialog } from './dialogs';
import { Avatar, Logo } from './ui';

// Ansicht für Externe ohne Konto: Zugriff über den Freigabe-Link /g/<token>.
// Optimiert fürs Smartphone und als Web-App auf dem Homescreen installierbar.

interface LinkInfo {
  branding?: Me['branding'];
  team: string;
  kind: 'aufgabe' | 'projekt';
  title: string;
  project: string;
  canWrite: boolean;
  expiresAt: string | null;
}

const storeKey = (link: string) => `machs.guest.${link}`;

function stored(link: string): string | null {
  try {
    return localStorage.getItem(storeKey(link));
  } catch {
    return null;
  }
}

function store(link: string, token: string | null) {
  try {
    if (token) localStorage.setItem(storeKey(link), token);
    else localStorage.removeItem(storeKey(link));
  } catch {
    // Privater Modus: gilt nur für diese Sitzung
  }
}

/** Manifest und Homescreen-Angaben für genau diesen Link setzen. */
function useInstallable(link: string, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const add = (tag: string, attrs: Record<string, string>) => {
      const el = document.createElement(tag);
      for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
      document.head.appendChild(el);
      return el;
    };
    const els = [
      add('link', { rel: 'manifest', href: `/api/guest/link/${link}/manifest.webmanifest` }),
      add('link', { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' }),
      add('meta', { name: 'apple-mobile-web-app-capable', content: 'yes' }),
      add('meta', { name: 'mobile-web-app-capable', content: 'yes' }),
      add('meta', { name: 'apple-mobile-web-app-title', content: "Mach's" }),
    ];
    return () => els.forEach((el) => el.remove());
  }, [link, enabled]);
}

/**
 * `link`: Freigabe-Link-Token. `joined`: ID einer Freigabe, der das eigene Konto
 * beigetreten ist (Zugang kommt dann vom Server statt aus dem Link).
 */
export function GuestApp({ link, joined }: { link: string; joined?: boolean }) {
  useInstallable(link, !joined);
  const [token, setToken] = useState<string | null>(() => (joined ? null : stored(link)));
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [state, setState] = useState<'laden' | 'beitreten' | 'bereit' | 'ungueltig'>('laden');

  const leave = useCallback(() => {
    if (!joined) store(link, null);
    setGuestToken(null);
    setToken(null);
    setMe(null);
  }, [link]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (token) {
        setGuestToken(token);
        try {
          const data = await api<Me>('/guest/me');
          await refreshUrlToken();
          if (cancelled) return;
          setMe(data);
          try {
            localStorage.setItem(`${storeKey(link)}.me`, JSON.stringify(data));
          } catch {
            // nur für den Offline-Start
          }
          setState('bereit');
          return;
        } catch (err) {
          // Offline weiterarbeiten
          const cached = (() => {
            try {
              return JSON.parse(localStorage.getItem(`${storeKey(link)}.me`) ?? 'null') as Me | null;
            } catch {
              return null;
            }
          })();
          if ((err as { status?: number }).status === 0 && cached) {
            if (!cancelled) (setMe(cached), setState('bereit'));
            return;
          }
          if (cancelled) return;
          leave();
          return;
        }
      }
      if (joined) {
        try {
          const r = await api<{ joined: { shareId: string; token: string | null; active: boolean }[] }>('/shares/joined');
          const entry = r.joined.find((j) => j.shareId === link && j.active && j.token);
          if (cancelled) return;
          if (entry?.token) setToken(entry.token);
          else setState('ungueltig');
        } catch {
          if (!cancelled) setState('ungueltig');
        }
        return;
      }
      try {
        const data = await api<LinkInfo>(`/guest/link/${link}`);
        if (cancelled) return;
        setInfo(data);
        setState('beitreten');
      } catch {
        if (!cancelled) setState('ungueltig');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, link, leave]);

  useEffect(() => {
    const invalid = () => leave();
    window.addEventListener('machs:guest-invalid', invalid);
    return () => window.removeEventListener('machs:guest-invalid', invalid);
  }, [leave]);

  if (state === 'laden') return <div className="boot"><div className="spinner" /></div>;
  if (state === 'ungueltig') return <Invalid />;
  if (state === 'beitreten' && info) {
    return (
      <Join
        link={link}
        info={info}
        onJoined={(t) => {
          store(link, t);
          setState('laden');
          setToken(t);
        }}
      />
    );
  }
  if (!me) return <div className="boot"><div className="spinner" /></div>;
  return (
    <SyncProvider key={me.user.id} me={me}>
      <GuestShell backToTeam={joined} />
    </SyncProvider>
  );
}

function BrandMark({ branding }: { branding?: Me['branding'] }) {
  return branding ? <div className="guest-brand-mark">{branding.logo && <img src={branding.logo} alt="" />}<b>{branding.name}</b></div> : <Logo size={40} claim />;
}
function brandStyle(branding?: Me['branding']): CSSProperties | undefined {
  return branding ? { '--brand-a': branding.color, '--brand-b': branding.color, '--brand': branding.color } as CSSProperties : undefined;
}
function Invalid() {
  return (
    <div className="guest-center">
      <Logo size={40} claim />
      <h1 className="display mt-8 text-[22px]">Dieser Link gilt nicht mehr</h1>
      <p className="mt-2 max-w-[340px] text-[var(--muted)]">
        Die Freigabe wurde beendet oder ist abgelaufen. Bitte frag bei der Person nach, die dir den Link geschickt hat.
      </p>
    </div>
  );
}

function Join({ link, info, onJoined }: { link: string; info: LinkInfo; onJoined: (token: string) => void }) {
  const [name, setName] = useState(() => {
    try {
      return localStorage.getItem('machs.guest.name') ?? '';
    } catch {
      return '';
    }
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Im Browser schon mit einem Mach's-Konto angemeldet? Dann mit dem Konto beitreten
  const hasAccount = hasSession();

  async function joinWithAccount() {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ sameTeam: boolean; taskId?: string | null; joined?: { token: string } }>('/shares/join', { method: 'POST', json: { token: link } });
      if (r.joined) return onJoined(r.joined.token);
      // Mitglied des freigebenden Teams: direkt zur Aufgabe
      location.href = r.taskId ? `/?aufgabe=${r.taskId}` : '/';
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (name.trim().length < 2) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ token: string }>(`/guest/link/${link}/join`, { method: 'POST', json: { name: name.trim() } });
      try {
        localStorage.setItem('machs.guest.name', name.trim());
      } catch {
        // egal
      }
      onJoined(r.token);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="guest-center" style={brandStyle(info.branding)}>
      <BrandMark branding={info.branding} />
      <div className="guest-card">
        <span className="text-[13px] font-semibold text-[var(--muted)]">{info.team} lädt dich ein</span>
        <h1 className="display mt-1 text-[22px] leading-tight">{info.title}</h1>
        {info.kind === 'aufgabe' && info.project && <span className="text-[13.5px] text-[var(--muted)]">{info.project}</span>}
        <p className="mt-4 text-[15px]">
          {info.canWrite
            ? info.kind === 'aufgabe'
              ? 'Schreib mit, schick Fotos, Videos und Sprachnachrichten direkt in diese Aufgabe.'
              : 'Du siehst alle Aufgaben dieses Projekts, schreibst mit und kannst neue Aufgaben anlegen.'
            : 'Du kannst den Verlauf ansehen, aber nicht mitschreiben.'}
          {' '}Ein Konto brauchst du nicht.
        </p>
        {hasAccount && (
          <>
            <button className="btn btn-primary mt-5 h-12" onClick={() => void joinWithAccount()} disabled={busy}>
              Mit meinem Mach's-Konto beitreten
            </button>
            <p className="mt-2 text-[12.5px] text-[var(--muted)]">Die {info.kind === 'aufgabe' ? 'Aufgabe erscheint' : 'Aufgaben erscheinen'} dann auch in deiner Mach's-App.</p>
            <div className="or-line"><span>oder ohne Konto</span></div>
          </>
        )}
        <form className="mt-5 flex flex-col gap-3" onSubmit={submit}>
          <label className="field">
            Wie heißt du?
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Vor- und Nachname" autoComplete="name" maxLength={60} required autoFocus />
          </label>
          {error && <p className="text-[13.5px] text-[var(--problem)]">{error}</p>}
          <button className={`btn ${hasAccount ? 'btn-ghost' : 'btn-primary'} h-12`} disabled={busy || name.trim().length < 2}>
            {busy ? 'Einen Moment …' : 'Loslegen'}
          </button>
        </form>
        <p className="mt-4 text-[12px] text-[var(--muted)]">
          Dein Name ist für alle in dieser {info.kind === 'aufgabe' ? 'Aufgabe' : 'Freigabe'} sichtbar. Deine Nachrichten sind als „extern“ gekennzeichnet.
        </p>
      </div>
    </div>
  );
}

function GuestShell({ backToTeam }: { backToTeam?: boolean }) {
  const { client, me, version } = useSync();
  const guest = me.guest!;
  const [taskId, setTaskId] = useState<string | null>(guest.taskId);
  const [creating, setCreating] = useState(false);
  const project = guest.projectId ? client.data.project.get(guest.projectId) : undefined;

  const tasks = useMemo(
    () => [...client.data.task.values()]
      .filter((t) => (guest.taskId ? t.id === guest.taskId : t.projectId === guest.projectId))
      .sort((a, b) => Number(a.status === 'erledigt') - Number(b.status === 'erledigt') || (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt)),
    [client, version, guest.taskId, guest.projectId],
  );

  const task = taskId ? client.data.task.get(taskId) : undefined;

  return (
    <div className="guest-shell" style={brandStyle(me.branding)}>
      {me.branding && <BrandMark branding={me.branding} />}
      {task ? (
        <div className="guest-chat">
          <ChatView
            key={task.id}
            task={task}
            onBack={guest.taskId ? undefined : () => setTaskId(null)}
            detailOpen={false}
            onToggleDetail={() => {}}
            onEditProject={() => {}}
          />
        </div>
      ) : guest.taskId ? (
        <div className="boot"><div className="spinner" /></div>
      ) : (
        <div className="guest-list">
          {backToTeam && (
            <a href="/" className="flex items-center gap-1.5 px-4 pt-3 text-[13px] font-semibold text-[var(--brand-a)] no-underline">
              <Icon name="back" size={15} />Zu meinem Team
            </a>
          )}
          <header className="flex items-center gap-3 px-4 pb-3 pt-4">
            <Avatar name={initials(project?.name ?? me.tenant.name)} gradient={avatarGradient(project?.id ?? me.tenant.id)} src={imageUrl(project?.imageMediaId)} size={44} square />
            <div className="min-w-0 flex-1">
              <b className="display block truncate text-[18px]">{project?.name ?? 'Projekt'}</b>
              <span className="text-[12.5px] text-[var(--muted)]">{backToTeam ? `Geteilt von ${me.tenant.name}` : me.tenant.name}</span>
            </div>
          </header>
          <InstallHint />
          <div className="scroll flex-1 px-2 pb-24">
            {tasks.length === 0 && <p className="mt-10 text-center text-[var(--muted)]">Noch keine Aufgaben in diesem Projekt.</p>}
            {tasks.map((t) => (
              <button key={t.id} className="row" onClick={() => setTaskId(t.id)}>
                <span className={`guest-dot t-${t.status}`} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <b className="flex-1 truncate text-[15px]">{t.title}</b>
                    <span className="text-[11.5px] text-[var(--muted)]">{listTime(t.lastMessageAt ?? t.createdAt)}</span>
                  </div>
                  <span className="block truncate text-[13px] text-[var(--muted)]">{t.lastMessagePreview ?? statusText(t.status)}</span>
                </div>
              </button>
            ))}
          </div>
          {guest.canWrite && (
            <button className="btn btn-primary guest-fab" onClick={() => setCreating(true)}>
              <Icon name="plus" size={20} />Neue Aufgabe
            </button>
          )}
        </div>
      )}
      {guest.taskId && task && <InstallHint floating />}
      {creating && guest.projectId && (
        <TaskDialog defaultBusinessId={null} defaultProjectId={guest.projectId} onClose={() => setCreating(false)} onCreated={setTaskId} />
      )}
    </div>
  );
}

type Prompt = Event & { prompt: () => Promise<void> };

/** Hinweis zum Installieren auf dem Homescreen (einmal pro Gerät ausblendbar). */
function InstallHint({ floating }: { floating?: boolean }) {
  const standalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem('machs.guest.install') === 'aus';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const onPrompt = (e: Event) => (e.preventDefault(), setPrompt(e as Prompt));
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  if (standalone || hidden || (!ios && !prompt)) return null;
  const close = () => {
    setHidden(true);
    try {
      localStorage.setItem('machs.guest.install', 'aus');
    } catch {
      // egal
    }
  };
  return (
    <div className={`guest-install ${floating ? 'floating' : ''}`}>
      <Icon name="plus" size={18} />
      <span className="flex-1">
        {ios ? (
          <>Als App nutzen: unten auf <b>Teilen</b> tippen, dann <b>Zum Home-Bildschirm</b>.</>
        ) : (
          <>Als App auf dem Homescreen nutzen.</>
        )}
      </span>
      {prompt && (
        <button className="btn btn-primary h-9 px-3" onClick={() => void prompt.prompt().then(close)}>Installieren</button>
      )}
      <button className="iconbtn plain" onClick={close} aria-label="Hinweis ausblenden"><Icon name="x" size={16} /></button>
    </div>
  );
}
